import { Admin, Resource, CustomRoutes } from "react-admin";
import simpleRestProvider from "ra-data-simple-rest";
import { authProvider } from "./authProvider";
import { httpClient } from "./httpClient";
import { Route } from "react-router-dom";
import { ClubList, ClubEdit, ClubCreate } from "./clubs";
import { Dashboard } from "./Dashboard";
import { BrowserRouter } from "react-router-dom";
import { UserList } from "./users/UserList";
import { UserInvite } from "./users/UserInvite";
import { UserEdit } from "./users/UserEdit";
import { TeamList } from "./teams/TeamList";
import { TeamCreate } from "./teams/TeamCreate";
import { TeamEdit } from "./teams/TeamEdit";
import { TeamAssignmentsPage } from "./teams/TeamAssignmentsPage";
import { ensureDefaultClubSelected } from "./club/bootstrapClub";
import { TrainingTemplateList, TrainingTemplateCreate, TrainingTemplateEdit } from "./trainingTemplates/";
import { TrainingSelectPage } from "./trainingPlay/TrainingSelectPage";
import { TrainingStartPage } from "./trainingPlay/TrainingStartPage";
import { TrainingPlayPage } from "./trainingPlay/TrainingPlayPage";
import CoachTrainingOverviewPage from "./trainingCoach/CoachTrainingOverviewPage";
import { ProfilePage } from "./profile/ProfilePage";
import { ClubDashboardPage } from "./trainingCoach/ClubDashboardPage";
import { PersonalDashboardPage } from "./trainingCoach/PersonalDashboardPage";

import TrainingTemplateTeamAssignmentsPage from "./trainingTemplates/TrainingTemplateTeamAssignmentsPage";
import MessageInboxPage from "./messages/MessageInboxPage";
import MessageBroadcastPage from "./messages/MessageBroadcastPage";
import { ChallengeTemplateList } from "./challenges/ChallengeTemplateList";
import { ChallengeList } from "./challenges/ChallengeList";
import { ChallengeEdit } from "./challenges/ChallengeEdit";
import { ChallengeCreate } from "./challenges/ChallengeCreate";
import ClubLeaderboardPage from "./club/ClubLeaderboardPage";

import { EventOccurrenceList } from "./events/EventOccurrenceList";
import { EventOccurrenceShow } from "./events/EventOccurrenceShow";
import { EventSeriesList } from "./events/EventSeriesList";
import { EventSeriesCreate } from "./events/EventSeriesCreate";
import { EventSeriesEdit } from "./events/EventSeriesEdit";
import { CalendarFeedList } from "./calendarFeeds/CalendarFeedList";
import { CalendarFeedCreate } from "./calendarFeeds/CalendarFeedCreate";
import { CalendarFeedEdit } from "./calendarFeeds/CalendarFeedEdit";
import CalendarPage from "./events/CalendarPage";

import RanksOverviewPage from "./points/RanksOverviewPage";
import { buildDataProvider } from "./dataProvider";
import { ShopItemList } from "./shop/admin/ShopItemList";
import { ShopItemCreate } from "./shop/admin/ShopItemCreate";
import { ShopItemEdit } from "./shop/admin/ShopItemEdit";
import { OrderList } from "./shop/admin/OrderList";
import ShopSettingsPage from "./shop/admin/ShopSettingsPage";
import ShopPage from "./shop/ShopPage";
import OrderConfirmationPage from "./shop/OrderConfirmationPage";
import MyOrdersPage from "./shop/MyOrdersPage";
import { PenaltyTypeList } from "./penalties/PenaltyTypeList";
import { PenaltyTypeCreate } from "./penalties/PenaltyTypeCreate";
import { PenaltyTypeEdit } from "./penalties/PenaltyTypeEdit";
import PenaltyList from "./penalties/PenaltyList";
import MyPenaltiesPage from "./penalties/MyPenaltiesPage";
import PenaltyPublicPage from "./penalties/PenaltyPublicPage";
import EventJobTypesPage from "./events/EventJobTypesPage";
import ClubNotificationRecipientsPage from "./notifications/ClubNotificationRecipientsPage";

import React from "react";
import { useClub } from "./club/ClubContext";
import { MyLayout } from "./layout/Layout";

import { CssBaseline } from "@mui/material";
import { glassLightTheme, glassDarkTheme } from "./theme-glass";
import { PushNavigationBridge } from "./push/PushNavigationBridge";

const apiUrl = "/api";
const baseProvider = simpleRestProvider(apiUrl, httpClient);

 const CLUB_SCOPED_RESOURCES = new Set<string>([
  "training-templates",
  "teams",
  "users",
]);

function withGlobalClubFilter(params: any, clubId: number | null) {
  if (!clubId) return params;
  return {
    ...params,
    filter: {
      ...(params?.filter ?? {}),
      clubId, // einheitlich "clubId"
    },
  };
}

export default function AdminApp() {
  const { clubId } = useClub();
  const [ready, setReady] = React.useState(false);



  React.useEffect(() => {
    (async () => {
      await ensureDefaultClubSelected();
      setReady(true);
    })();
  }, []);

  const dataProvider = React.useMemo(() => {
    return buildDataProvider({ clubId, ready }) as any;
  }, [clubId, ready]);

  // ✅ return ERST nach allen Hooks
  if (!ready) return null; // oder <Loading />

  return (
      <>
      <BrowserRouter>
      <PushNavigationBridge />
      <CssBaseline />
    <Admin authProvider={authProvider} dataProvider={dataProvider} layout={MyLayout} lightTheme={glassLightTheme}
        darkTheme={glassDarkTheme} dashboard={Dashboard}
        defaultTheme="light">
      <Resource name="clubs" list={ClubList} edit={ClubEdit} create={ClubCreate} />
      <Resource name="users" list={UserList} create={UserInvite} edit={UserEdit} />
      <Resource name="teams" list={TeamList} edit={TeamEdit} create={TeamCreate} />
      <Resource name="training-templates" list={TrainingTemplateList} create={TrainingTemplateCreate} edit={TrainingTemplateEdit} />
      <Resource name="challenge-templates" list={ChallengeTemplateList} />
      <Resource name="challenges" list={ChallengeList} edit={ChallengeEdit} create={ChallengeCreate} />
      <Resource name="events/series" list={EventSeriesList} edit={EventSeriesEdit} create={EventSeriesCreate} />
      <Resource name="events/occurrences" list={EventOccurrenceList} show={EventOccurrenceShow} />
      <Resource name="calendar-feeds" list={CalendarFeedList} create={CalendarFeedCreate} edit={CalendarFeedEdit} />
      <Resource name="shop-items" list={ShopItemList} create={ShopItemCreate} edit={ShopItemEdit} />
      <Resource name="shop-orders" list={OrderList} />
      <Resource name="penalty-types" list={PenaltyTypeList} create={PenaltyTypeCreate} edit={PenaltyTypeEdit} />
      <CustomRoutes>
        <Route path="/team-assignments" element={<TeamAssignmentsPage />} />
        <Route path="/play/training" element={<TrainingSelectPage />} />
        <Route path="/play/training/:templateId" element={<TrainingStartPage />} />
        <Route path="/play/session/:sessionId" element={<TrainingPlayPage />} />
        <Route path="/coach/training" element={<CoachTrainingOverviewPage />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/club-dashboard" element={<ClubDashboardPage />} />
        <Route path="/personal-dashboard" element={<PersonalDashboardPage />} />
        <Route path="/training-template-teams" element={<TrainingTemplateTeamAssignmentsPage />} />
        <Route path="/inbox" element={<MessageInboxPage />} />
        <Route path="/admin/broadcast" element={<MessageBroadcastPage />} />
        <Route path="/club/leaderboard" element={<ClubLeaderboardPage />} />
        <Route path="/ranks" element={<RanksOverviewPage />} />
        <Route path="/calendar" element={<CalendarPage />} />
        <Route path="/shop" element={<ShopPage />} />
        <Route path="/shop/order/:token" element={<OrderConfirmationPage />} />
        <Route path="/shop/my-orders" element={<MyOrdersPage />} />
        <Route path="/shop-settings" element={<ShopSettingsPage />} />
        <Route path="/penalties" element={<PenaltyList />} />
        <Route path="/penalties/my" element={<MyPenaltiesPage />} />
        <Route path="/penalties/catalog" element={<PenaltyPublicPage />} />
        <Route path="/events/job-types" element={<EventJobTypesPage />} />
        <Route path="/club/notifications" element={<ClubNotificationRecipientsPage />} />
      </CustomRoutes>


    </Admin>
    </BrowserRouter>
     </>
  );
}
